"use client";

import * as yup from "yup";
import { ChangeEvent, useState } from "react";
import { TextField, Button, Grid } from "@mui/material";
import useToast from "@/stores/toast";
import axios from "axios";
import config from "@/app.config";
import { getMessage, getYupErrorMessages } from "@/lib/error";

const schema = yup.object({
  name: yup
    .string()
    .required("Name is required")
    .max(255, "Name must not exceed 255 characters"),
  email: yup
    .string()
    .required("Email is required")
    .email("Invalid email address")
    .max(255, "Email must not exceed 255 characters")
    .transform((value) => (value ? value.toLowerCase() : "")),
  phone: yup
    .string()
    .required("Phone number is required")
    .matches(/^[0-9]+$/, "Phone number must contain only digits")
    .length(10, "Phone number must be exactly 10 digits"),
  message: yup
    .string()
    .required("Message is required")
    .max(5000, "Message must not exceed 5000 characters"),
});

export default function Form() {
  const INITIAL_VALUE = {
    name: "",
    phone: "",
    email: "",
    message: "",
  };
  const { toast } = useToast();
  const [form, setForm] = useState(INITIAL_VALUE);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setErrors({});

      await schema.validate(form, { abortEarly: false });

      await axios.post(`${config.app.apiBaseUrl}/api/v2/services`, form, {
        headers: {
          Accept: "application/json",
        },
        withCredentials: true,
      });

      toast({
        error: false,
        message: "Thank you for contacting us. We will get back to you soon.",
      });

      setForm(INITIAL_VALUE);
    } catch (error: unknown) {
      const errors = getYupErrorMessages(error);

      if (errors) {
        setErrors(errors);
        return;
      }

      toast({
        error: true,
        message: getMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto max-w-5xl p-6 md:p-8 md:rounded-2xl bg-white">
      <Grid container spacing={{ xs: 2, md: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <TextField
            label="Full Name"
            name="name"
            variant="outlined"
            value={form.name}
            onChange={handleChange}
            required
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <TextField
            label="Phone Number"
            name="phone"
            variant="outlined"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            required
            error={!!errors.phone}
            helperText={errors.phone}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
          <TextField
            label="Email Address"
            name="email"
            variant="outlined"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
          <TextField
            label="Message"
            name="message"
            variant="outlined"
            value={form.message}
            onChange={handleChange}
            multiline
            rows={3}
            required
            error={!!errors.message}
            helperText={errors.message}
            fullWidth
          />
        </Grid>
      </Grid>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        sx={{ mt: 3 }}
        disabled={loading}
        loading={loading}
        onClick={handleSubmit}
      >
        Send Message
      </Button>
    </section>
  );
}
